import { match } from 'ts-pattern'
import { joinURL } from 'ufo'
import type {
  Media,
  TaskBaseFragment,
} from 'src/services/apollo/generated/graphql'
import { getApiUrl } from './config'
import { isVideoTask } from './task'

export function getMediaUrl(
  mediaId: string | undefined,
  quality: 'orig' | 'thumbnail' | 'still_thumbnail',
) {
  if (!mediaId) return undefined
  const postfix = match(quality)
    .with('thumbnail', () => 'thumbnail')
    .otherwise(_value => 'image')
  return joinURL(getApiUrl(), 'v1', 'media', mediaId, postfix)
}

export const getImagePublicUrl = (mediaId?: string, media?: Media) => {
  const inMedia = getPublicUrl(media)
  if (inMedia) {
    return inMedia
  }
  return getMediaUrl(mediaId, 'orig')
}

export const getImageThumbnailUrl = (mediaId?: string, media?: Media) => {
  const inMedia = getThumbnailUrl(media)
  if (inMedia) {
    return inMedia
  }
  return getMediaUrl(mediaId, 'thumbnail')
}

export const getImageStillThumbnailUrl = (mediaId?: string, media?: Media) => {
  const inMedia = getStillThumbnailUrl(media)
  if (inMedia) {
    return inMedia
  }
  return getMediaUrl(mediaId, 'still_thumbnail')
}

export const getPublicUrl = (media?: Media) =>
  media?.urls?.find(url => url.variant === 'PUBLIC')?.url

export const getThumbnailUrl = (media?: Media) =>
  media?.urls?.find(url => url.variant === 'THUMBNAIL')?.url ??
  media?.thumbnailUrl ??
  undefined

/**
 * Like `getThumbnailUrl`, but does NOT fall back to the synthesized
 * `media.thumbnailUrl` (which resolves to `/v1/media/<id>/thumbnail`). That
 * endpoint 404s for non-image media such as videos, so for reference-video
 * thumbnails we must rely only on the resolved `urls[THUMBNAIL]` variant
 * and let the caller fall back to the oRPC generate-or-get-thumbnail flow.
 */
export const getThumbnailVariantUrl = (media?: Media) =>
  media?.urls?.find(url => url.variant === 'THUMBNAIL')?.url ?? undefined

export const getStillThumbnailUrl = (media?: Media) =>
  media?.urls?.find(url => url.variant === 'STILL_THUMBNAIL')?.url ??
  media?.thumbnailUrl ??
  undefined

export const getVideoMediaIdFromTask = (
  task?: Pick<TaskBaseFragment, 'parameters' | 'outputs'>,
): string | undefined =>
  isVideoTask(task) ? task?.outputs?.videos?.[0]?.mediaId : undefined

export const getSmartThumbnailUrl = (media?: Media, preferStill?: boolean) => {
  if (!media) return undefined
  const publicUrl = getPublicUrl(media)
  const thumbnailUrl = getThumbnailUrl(media)
  // replace 'public' with resizing option
  if (publicUrl?.includes('public')) {
    const { width } = media
    const variant = !width || width > 1000 ? 'thumbnail' : `w=${width - 1}`
    return publicUrl.replace('public', variant)
  }
  // nothing to replace
  return (preferStill ? getStillThumbnailUrl(media) : null) || thumbnailUrl
}

export const getAspectRatio = (media?: Pick<Media, 'height' | 'width'>) =>
  media?.width && media?.height ? media.width / media.height : 1

export const downloadMedia = async (media?: Media) => {
  if (!media) return
  const url = getPublicUrl(media)
  if (!url) return
  const res = await fetch(url)
  const blob = await res.blob()
  const file = new File([blob], media.id, {
    type: media.imageType ?? blob.type,
  })
  return file
}

export function shouldMediaBeBlurred(media?: Media) {
  return media?.flag?.shouldBlur ?? false
}
